<template>
  <div class="searchable_multi_select" :class="{ open }" @click.stop>
    <button type="button" class="multi_select_trigger" @click="open = !open" :aria-expanded="open">
      <span class="multi_select_content">
        <span v-if="modelValue.length === 0" class="multi_select_placeholder">{{ placeholder }}</span>
        <span v-else class="multi_select_tags">
          <span v-for="v in modelValue" :key="v" class="multi_select_tag">
            <span class="multi_select_tag_text">{{ getLabelForValue(v) }}</span>
            <button type="button" class="multi_select_tag_remove" @click.stop="remove(v)" aria-label="Odstrani">×</button>
          </span>
        </span>
      </span>
      <span class="multi_select_arrow" aria-hidden="true">▼</span>
    </button>
    <div v-show="open" class="multi_select_panel">
      <input v-model="searchQuery" type="text" class="multi_select_input" placeholder="Išči…" @click.stop autocomplete="off" />
      <ul class="multi_select_options" role="listbox">
        <li v-for="opt in filteredOptions" :key="getValue(opt)" class="multi_select_option" role="option" :aria-selected="isSelected(opt)" @click="toggle(opt)">
          <input type="checkbox" :checked="isSelected(opt)" tabindex="-1" readonly class="multi_select_option_checkbox" />
          <span class="multi_select_option_text">{{ getLabel(opt) }}</span>
        </li>
        <li v-if="filteredOptions.length === 0" class="multi_select_empty">Ni zadetkov</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchableMultiSelect',
  props: {
    options: { type: Array, default: () => [] },
    valueKey: { type: String, default: 'value' },
    labelKey: { type: String, default: 'label' },
    modelValue: { type: Array, default: () => [] },
    placeholder: { type: String, default: 'Išči…' }
  },
  emits: ['update:modelValue'],
  data() {
    return { open: false, searchQuery: '' };
  },
  created() {
    this._boundClose = () => { this.open = false; };
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.$nextTick(() => document.addEventListener('click', this._boundClose));
      } else {
        document.removeEventListener('click', this._boundClose);
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this._boundClose);
  },
  computed: {
    filteredOptions() {
      const q = (this.searchQuery || '').toLowerCase().trim();
      if (!q) return this.options;
      return this.options.filter(o => this.getLabel(o).toLowerCase().includes(q));
    },
    selectedSet() {
      return new Set(this.modelValue);
    }
  },
  methods: {
    getValue(o) {
      if (o == null) return o;
      return this.valueKey && typeof o === 'object' ? o[this.valueKey] : o;
    },
    getLabel(o) {
      if (o == null) return '';
      if (this.labelKey && typeof o === 'object') return o[this.labelKey] != null ? String(o[this.labelKey]) : '';
      return String(o);
    },
    getLabelForValue(v) {
      const opt = this.options.find(o => this.getValue(o) === v);
      return opt != null ? this.getLabel(opt) : v;
    },
    toggle(opt) {
      const v = this.getValue(opt);
      const set = new Set(this.modelValue);
      if (set.has(v)) set.delete(v);
      else set.add(v);
      this.$emit('update:modelValue', [...set]);
    },
    remove(v) {
      this.$emit('update:modelValue', this.modelValue.filter(x => x !== v));
    },
    isSelected(opt) {
      return this.selectedSet.has(this.getValue(opt));
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/scss/basic/colors.scss";

.searchable_multi_select {
  position: relative;
  width: 100%;
  font-size: 0.9rem;
}
.multi_select_trigger {
  width: 100%;
  min-height: 2.5rem;
  padding: 0.5rem;
  text-align: left;
  border: 1px solid $border_color;
  border-radius: 0;
  background: $color_white;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &:focus {
    outline: none;
  }
}
.searchable_multi_select.open .multi_select_trigger {
  border-bottom-color: transparent;
}
.multi_select_content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  align-items: center;
}
.multi_select_placeholder {
  color: $text_color;
}
.multi_select_tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}
.multi_select_tag {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.15rem 0.25rem 0.15rem 0.4rem;
  background: $color_gray_lighter;
  border-radius: 0;
  font-size: 0.85rem;
  line-height: 1.2;
  color: $text_color;
}
.multi_select_tag_text {
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.multi_select_tag_remove {
  padding: 0 0.15rem;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1.1em;
  line-height: 1;
  color: $text_color;
  &:hover { color: $color_gray_dark; background: $color_gray_light; }
}
.multi_select_arrow {
  flex-shrink: 0;
  font-size: 1em;
  color: $text_color;
  transition: transform 0.2s ease;
}
.searchable_multi_select.open .multi_select_arrow {
  transform: rotate(-180deg);
}
.multi_select_panel {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: -1px;
  border: 1px solid $border_color;
  border-top: none;
  border-radius: 0;
  background: $color_white;
  z-index: 25;
  box-shadow: 0 2px 4px 0 rgba(187, 187, 187, 0.5);
}
.multi_select_input {
  width: 100%;
  padding: 0.5rem;
  border: none;
  border-bottom: 1px solid $color_gray_light;
  font-size: inherit;
  font-family: inherit;
  box-sizing: border-box;
  background: $color_white;
  color: $text_color;
  &::placeholder { color: $text_color; }
  &:focus { outline: none; }
}
.multi_select_options {
  list-style: none;
  margin: 0;
  padding: 0.25rem 0;
  max-height: 12rem;
  overflow-y: auto;
}
.multi_select_option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  font-weight: normal;
  color: $text_color;
  &:hover { background: $color_gray_lighter; }
}
.multi_select_options > li.multi_select_empty {
  cursor: default;
  color: $text_color;
  font-size: 0.9em;
  padding: 0.6rem 0.5rem;
  list-style: none;
  &:hover { background: transparent; }
}
.multi_select_option_checkbox {
  flex-shrink: 0;
  margin: 0;
  cursor: pointer;
  pointer-events: none;
}
.multi_select_option_text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
